import { useState, useEffect } from 'react'
import { CAMPAIGN_TYPE_OPTIONS } from '../constants/campaignTypes'
import { getRevenuePerDay, DashboardFilters, RevenuePerDayData } from '../services/api'
import LoadingSpinner from '../components/LoadingSpinner'
import { ExportButton } from '../components/ExportButton'

const RevenuePerDay = () => {
  const [data, setData] = useState<RevenuePerDayData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filters, setFilters] = useState({
    startDate: '2025-09-01',
    endDate: '2025-11-30',
    type: 'all',
    flow: 'all'
  })

  const fetchData = async () => {
    try {
      setLoading(true)

      const apiFilters: DashboardFilters = {
        startDate: filters.startDate,
        endDate: filters.endDate,
        type: filters.type,
        flow: filters.flow
      }

      const response = await getRevenuePerDay(apiFilters)
      setData(response.data)
      setError(null)

      console.log('💰 Revenue Per Day data loaded:', response.data)
    } catch (err) {
      setError('Error al cargar los datos de revenue por día')
      console.error('Error fetching revenue per day data:', err)
    } finally {
      setLoading(false)
    }
  }

  const resetAllFilters = () => {
    console.log('Reseteando todos los filtros')
    const defaultFilters = {
      startDate: '2025-09-01',
      endDate: '2025-11-30',
      type: 'all',
      flow: 'all'
    }
    setFilters(defaultFilters)
  }

  useEffect(() => {
    fetchData()
  }, [filters])

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }))
  }

  if (loading) {
    return <LoadingSpinner />
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="text-red-500 text-xl mb-2">⚠️</div>
          <p className="text-dashboard-muted">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="btn-primary mt-4"
          >
            Reintentar
          </button>
        </div>
      </div>
    )
  }

  if (!data) {
    return <div>No hay datos disponibles</div>
  }

  // dailyData viene agrupado por día + campaña (una campaña define un único
  // "tipo" sin ambigüedad), así que un mismo día puede tener varias filas.
  // Para los totales "por día" hay que agregar por fecha, no usar
  // dailyData.length directamente (contaría de más si hay >1 campaña/día).
  const revenueByDate = data.dailyData.reduce<Record<string, number>>((acc, day) => {
    acc[day.date] = (acc[day.date] || 0) + day.revenue
    return acc
  }, {})
  const uniqueDaysCount = Object.keys(revenueByDate).length
  const bestDayRevenue = uniqueDaysCount > 0 ? Math.max(...Object.values(revenueByDate)) : 0

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-dashboard-text flex items-center">
            <span className="mr-3">💰</span>
            REVENUE PER DAY
          </h1>
          <p className="text-dashboard-muted mt-1">
            Análisis temporal de ingresos diarios
          </p>
        </div>
        <div className="flex items-center space-x-4">
          <ExportButton 
            type="revenue-per-day"
            filters={filters}
          />
          <div className="text-right">
            <div className="text-sm text-dashboard-muted">Período</div>
            <div className="text-sm font-semibold text-dashboard-text">
              {filters.startDate} - {filters.endDate}
            </div>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="filter-container">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-dashboard-text">Filtros de Análisis</h3>
          <button 
            onClick={resetAllFilters}
            className="btn-secondary text-sm"
          >
            Resetear
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-dashboard-text mb-2">
              TYPE
            </label>
            <select
              value={filters.type}
              onChange={(e) => handleFilterChange('type', e.target.value)}
              className="filter-select w-full"
            >
              {CAMPAIGN_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-dashboard-text mb-2">
              Flow
            </label>
            <select
              value={filters.flow}
              onChange={(e) => handleFilterChange('flow', e.target.value)}
              className="filter-select w-full"
            >
              <option value="all">Todos los flows</option>
              <option value="seguro-salud">Seguro Salud</option>
              <option value="creditos-personales">Créditos Personales</option>
              <option value="tarjeta-credito">Tarjeta de Crédito</option>
              <option value="seguro-decesos">Seguro Decesos</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-dashboard-text mb-2">
              Fecha Inicio
            </label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
              className="filter-select w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-dashboard-text mb-2">
              Fecha Fin
            </label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
              className="filter-select w-full"
            />
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="metric-card-premium">
          <div className="flex items-center justify-between mb-4">
            <div className="metric-label-premium">TOTAL REVENUE</div>
            <div className="text-2xl">💎</div>
          </div>
          <div className="metric-value-premium">€{data.totalRevenue.toFixed(2)}</div>
          <div className="text-sm text-globo-100 mt-1">Ingresos totales del período</div>
        </div>

        <div className="metric-card">
          <div className="flex items-center justify-between mb-4">
            <div className="metric-label">TOTAL LEADS</div>
            <div className="text-2xl">👥</div>
          </div>
          <div className="metric-value">{data.totalLeads.toLocaleString()}</div>
          <div className="text-sm text-dashboard-muted mt-1">Leads generados</div>
        </div>

        <div className="metric-card">
          <div className="flex items-center justify-between mb-4">
            <div className="metric-label">AVG VALIDATION</div>
            <div className="text-2xl">✅</div>
          </div>
          <div className="metric-value">{(data.avgValidationRate * 100).toFixed(1)}%</div>
          <div className="text-sm text-dashboard-muted mt-1">Tasa de validación promedio</div>
        </div>
      </div>

      {/* Daily Revenue Table */}
      <div className="card">
        <div className="p-6 border-b border-dashboard-border">
          <h2 className="text-xl font-bold text-dashboard-text flex items-center justify-between">
            <span className="flex items-center">
              <span className="mr-2">📅</span>
              DAILY REVENUE BREAKDOWN
            </span>
            <span className="text-sm font-normal text-dashboard-muted">
              {uniqueDaysCount} días
            </span>
          </h2>
        </div>
        <div className="overflow-x-auto">
          <table className="table-modern">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Type</th>
                <th>Revenue</th>
                <th>Leads</th>
                <th>Validation Rate</th>
                <th>Revenue per Lead</th>
              </tr>
            </thead>
            <tbody>
              {data.dailyData.map((day, index) => {
                // Función para determinar el tipo basado en el nombre de la campaña
                const getCampaignType = (campaignName: string) => {
                  const name = (campaignName || '').toUpperCase();
                  if (name.includes('CLICKOUT')) return 'CLICKOUT';
                  if (name.includes('SPONSOR')) return 'SPONSOR';
                  if (name.includes('COREGISTRO')) return 'COREGISTRO';
                  if (name.includes('MARKETPLACE')) return 'MARKETPLACE';
                  return 'OTHER';
                };

                return (
                  <tr key={index}>
                    <td className="font-semibold">
                      {new Date(day.date).toLocaleDateString('es-ES', {
                        weekday: 'short',
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </td>
                    <td>
                      <span className={`badge ${
                        getCampaignType(day.campaign || '') === 'CLICKOUT' ? 'badge-info' :
                        getCampaignType(day.campaign || '') === 'SPONSOR' ? 'badge-info' :
                        getCampaignType(day.campaign || '') === 'COREGISTRO' ? 'badge-warning' :
                        getCampaignType(day.campaign || '') === 'MARKETPLACE' ? 'badge-error' :
                        'badge-secondary'
                      }`}>
                        {getCampaignType(day.campaign || '')}
                      </span>
                    </td>
                    <td className="font-semibold text-green-600">
                      €{day.revenue.toFixed(2)}
                    </td>
                  <td className="font-semibold">
                    {day.leads.toLocaleString()}
                  </td>
                  <td>
                    <div className="flex items-center">
                      <div className="w-12 bg-gray-200 rounded-full h-2 mr-2">
                        <div 
                          className="bg-green-500 h-2 rounded-full" 
                          style={{ width: `${day.validationRate * 100}%` }}
                        ></div>
                      </div>
                      <span className="text-sm">{(day.validationRate * 100).toFixed(1)}%</span>
                    </div>
                  </td>
                    <td className="font-semibold">
                      €{day.leads > 0 ? (day.revenue / day.leads).toFixed(2) : '0.00'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        
        {/* Table Summary */}
        <div className="p-6 border-t border-dashboard-border bg-dashboard-bg-dark">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-center">
            <div>
              <div className="text-sm text-dashboard-muted">Días Analizados</div>
              <div className="text-lg font-bold text-dashboard-text">{uniqueDaysCount}</div>
            </div>
            <div>
              <div className="text-sm text-dashboard-muted">Revenue Promedio/Día</div>
              <div className="text-lg font-bold text-green-600">
                €{uniqueDaysCount > 0 ? (data.totalRevenue / uniqueDaysCount).toFixed(2) : '0.00'}
              </div>
            </div>
            <div>
              <div className="text-sm text-dashboard-muted">Leads Promedio/Día</div>
              <div className="text-lg font-bold text-dashboard-text">
                {uniqueDaysCount > 0 ? Math.round(data.totalLeads / uniqueDaysCount) : 0}
              </div>
            </div>
            <div>
              <div className="text-sm text-dashboard-muted">Mejor Día</div>
              <div className="text-lg font-bold text-green-600">
                €{bestDayRevenue.toFixed(2)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default RevenuePerDay
